import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  message,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Tabs,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Space,
  Badge,
  Typography,
  Spin,
  Empty
} from 'antd';
import {
  SearchOutlined,
  DollarOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  CopyOutlined,
  ReloadOutlined,
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  FormOutlined,
  DownOutlined
} from '@ant-design/icons';
import { Dropdown } from 'antd';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius, typography, shadows, transitions } from '../../styles/monochromeTheme';
import dayjs from 'dayjs';

// VoxPay service - chargé dynamiquement pour éviter les erreurs si non configuré
let voxpayService = null;
try {
  const { VoxPayService } = require('../../services/voxpayService');
  voxpayService = new VoxPayService();
} catch (error) {
  console.warn('VoxPay service not configured:', error.message);
}

const { Text, Title } = Typography;
const { Option } = Select;

const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxxxl,
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xxxl,
    paddingBottom: spacing.xxl,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  headerIcon: {
    width: '56px',
    height: '56px',
    background: colors.bgDark,
    borderRadius: radius.xxl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
  },
  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    letterSpacing: typography.letterSpacing.tight,
  },
  headerSubtitle: {
    margin: 0,
    fontSize: typography.fontSize.md,
    color: colors.textTertiary,
    marginTop: spacing.xs,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.lg,
    marginBottom: spacing.xxxl,
  },
  statCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xxl,
    padding: spacing.xl,
    border: `1px solid ${colors.borderLight}`,
  },
  tableCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xxl,
    border: `1px solid ${colors.borderLight}`,
    overflow: 'hidden',
  },
  tableHeader: {
    padding: spacing.xl,
    borderBottom: `1px solid ${colors.borderLight}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.lg,
    flexWrap: 'wrap',
  },
  actionBtn: {
    height: '36px',
    borderRadius: radius.lg,
    fontWeight: typography.fontWeight.medium,
  },
  paymentLink: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    background: colors.bgTertiary,
    borderRadius: radius.lg,
    marginTop: spacing.md,
  },
};

const PaymentTracking = () => {
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('contracts');

  // Modal states
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentForm] = Form.useForm();

  // Workflow modal states
  const [workflowModalVisible, setWorkflowModalVisible] = useState(false);
  const [workflowMode, setWorkflowMode] = useState('unified'); // 'unified', 'signature', 'payment'
  const [generatedWorkflowLink, setGeneratedWorkflowLink] = useState(null);
  const [workflowForm] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les documents/contrats
      const { data: docsData, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (docsError) throw docsError;

      // Charger les paiements
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('contract_payments')
        .select('*')
        .order('created_at', { ascending: false });

      // Si la table n'existe pas, on continue sans erreur
      if (paymentsError && !paymentsError.message.includes('does not exist')) {
        console.error('Error loading payments:', paymentsError);
      }

      // Grouper les documents par charter_id
      const grouped = {};
      (docsData || []).forEach(doc => {
        const key = doc.charter_id || doc.id;
        if (!grouped[key]) {
          grouped[key] = {
            charter_id: key,
            client_name: doc.client_name,
            client_email: doc.client_email || '',
            charter_date: doc.charter_date,
            created_at: doc.created_at,
            category: doc.category || 'bateaux',
            total_amount: doc.total_amount || 0,
            documents: {},
            payment_status: 'pending'
          };
        }
        grouped[key].documents[doc.type] = doc;
        // Prendre le montant du contrat s'il existe
        if (doc.type === 'Contrat' && doc.total_amount) {
          grouped[key].total_amount = doc.total_amount;
        }
      });

      // Associer les paiements aux documents
      const groupedArray = Object.values(grouped).map(charter => {
        const charterPayments = (paymentsData || []).filter(
          p => p.charter_id === charter.charter_id
        );
        const latestPayment = charterPayments[0];
        return {
          ...charter,
          payments: charterPayments,
          payment_status: latestPayment?.status || 'pending',
          payment_link: latestPayment?.payment_link || null,
          voxpay_id: latestPayment?.voxpay_id || null
        };
      });

      setDocuments(groupedArray);
      setPayments(paymentsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Stats calculées
  const stats = useMemo(() => {
    const total = documents.length;
    const pending = documents.filter(d => d.payment_status === 'pending').length;
    const paid = documents.filter(d => d.payment_status === 'paid').length;
    const linkSent = documents.filter(d => d.payment_status === 'link_sent').length;
    const totalAmount = documents.reduce((sum, d) => sum + (d.total_amount || 0), 0);
    const paidAmount = documents
      .filter(d => d.payment_status === 'paid')
      .reduce((sum, d) => sum + (d.total_amount || 0), 0);

    return { total, pending, paid, linkSent, totalAmount, paidAmount };
  }, [documents]);

  // Filtrer les documents
  const filteredDocuments = useMemo(() => {
    let result = documents;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.client_name?.toLowerCase().includes(query) ||
        d.client_email?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(d => d.payment_status === statusFilter);
    }

    return result;
  }, [documents, searchQuery, statusFilter]);

  // Ouvrir modal de paiement
  const openPaymentModal = (record) => {
    setSelectedDocument(record);
    paymentForm.setFieldsValue({
      amount: record.total_amount || 0,
      client_email: record.client_email || '',
      client_name: record.client_name || '',
      description: `Paiement - ${record.client_name} - ${dayjs(record.charter_date).format('DD/MM/YYYY')}`
    });
    setPaymentModalVisible(true);
  };

  // Générer le lien de paiement VoxPay
  const handleGeneratePaymentLink = async (values) => {
    if (!selectedDocument) return;

    // Vérifier si VoxPay est configuré
    if (!voxpayService) {
      message.error('VoxPay non configuré. Vérifiez les variables d\'environnement.');
      return;
    }

    setGeneratingLink(true);
    try {
      // Générer un uniqueLink pour les URLs de retour
      const uniqueLink = `payment-${selectedDocument.charter_id}-${Date.now()}`;

      // Formater la date pour la référence
      const formattedDate = selectedDocument.charter_date
        ? dayjs(selectedDocument.charter_date).format('DD/MM/YYYY')
        : '';

      // Catégorie lisible
      const categoryLabels = {
        bateaux: 'Yacht',
        jetski: 'Jetski',
        seabobs: 'Seabob',
        lunch: 'Lunch',
        custom: 'Charter'
      };
      const categoryLabel = categoryLabels[selectedDocument.category] || 'Charter';

      // Créer le paiement via VoxPay
      const paymentData = {
        amount: values.amount,
        customerEmail: values.client_email,
        customerName: values.client_name,
        // Description claire pour le client
        description: `Master Ski Pilou - ${categoryLabel} ${formattedDate} - ${values.client_name}`,
        // Référence lisible: MSP-YACHT-12/01/2025-DUPONT
        reference: `MSP-${categoryLabel.toUpperCase()}-${formattedDate.replace(/\//g, '')}-${values.client_name.split(' ')[0].toUpperCase().substring(0, 8)}`,
        // Champs requis par VoxPayService
        reservationId: selectedDocument.charter_id || 'N/A',
        uniqueLink: uniqueLink,
        charterDate: formattedDate,
        boatName: categoryLabel,
        tripType: selectedDocument.category || 'charter',
        metadata: {
          charter_id: selectedDocument.charter_id,
          charter_date: selectedDocument.charter_date,
          client_name: values.client_name
        }
      };

      const result = await voxpayService.createPaymentLink(paymentData);

      if (result.success) {
        // Sauvegarder le paiement en base
        const { error: insertError } = await supabase
          .from('contract_payments')
          .insert({
            charter_id: selectedDocument.charter_id,
            client_name: values.client_name,
            client_email: values.client_email,
            amount: values.amount,
            currency: 'EUR',
            payment_link: result.paymentUrl,
            voxpay_id: result.voxpayId,
            transaction_id: result.transactionId,
            status: 'link_sent',
            created_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error saving payment:', insertError);
          // Continuer même si la sauvegarde échoue
        }

        message.success('Lien de paiement généré avec succès !');
        setPaymentModalVisible(false);

        // Copier le lien
        if (result.paymentUrl) {
          navigator.clipboard.writeText(result.paymentUrl);
          message.info('Lien copié dans le presse-papiers');
        }

        loadData(); // Rafraîchir les données
      } else {
        throw new Error(result.error || 'Erreur lors de la génération du lien');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      message.error(`Erreur: ${error.message}`);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copier le lien de paiement
  const copyPaymentLink = (link) => {
    navigator.clipboard.writeText(link);
    message.success('Lien copié !');
  };

  // Marquer comme payé manuellement
  const markAsPaid = async (record) => {
    try {
      const { error } = await supabase
        .from('contract_payments')
        .update({ status: 'paid', paid_at: new Date().toISOString() })
        .eq('charter_id', record.charter_id);

      if (error) throw error;
      message.success('Marqué comme payé');
      loadData();
    } catch (error) {
      message.error('Erreur: ' + error.message);
    }
  };

  // Convertir URL Google Drive en URL de téléchargement direct
  const getGoogleDriveDownloadUrl = (url) => {
    if (!url) return null;

    // Si c'est déjà une URL Supabase, la retourner
    if (url.includes('supabase')) return url;

    // Extraire l'ID du fichier Google Drive
    const driveMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (driveMatch) {
      const fileId = driveMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    return url;
  };

  // Télécharger PDF depuis Google Drive et uploader sur Supabase
  const uploadPdfToSupabase = async (googleDriveUrl, uniqueLink, clientName) => {
    try {
      const downloadUrl = getGoogleDriveDownloadUrl(googleDriveUrl);
      if (!downloadUrl) return null;

      // Si c'est déjà sur Supabase, pas besoin de re-uploader
      if (downloadUrl.includes('supabase')) {
        return downloadUrl;
      }

      message.loading('Téléchargement du contrat depuis Google Drive...', 0);

      // Télécharger le PDF
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Erreur téléchargement: ${response.status}`);
      }

      const pdfBlob = await response.blob();
      const fileName = `contracts/${uniqueLink}_${clientName.replace(/\s+/g, '_')}.pdf`;

      message.destroy();
      message.loading('Upload vers Supabase...', 0);

      // Uploader sur Supabase Storage
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (error) throw error;

      // Obtenir l'URL publique
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(fileName);

      message.destroy();
      message.success('Contrat uploadé sur Supabase');

      return urlData.publicUrl;

    } catch (error) {
      message.destroy();
      console.error('Erreur upload PDF:', error);
      message.warning('Impossible d\'uploader le PDF - la signature manuelle sera utilisée');
      return null;
    }
  };

  // Ouvrir modal workflow avec mode spécifique
  const openWorkflowModal = (record, mode) => {
    setSelectedDocument(record);
    setWorkflowMode(mode);
    setGeneratedWorkflowLink(null);
    workflowForm.setFieldsValue({
      client_name: record.client_name || '',
      client_email: record.client_email || '',
      amount: record.total_amount || 0,
      description: `Contrat - ${record.client_name} - ${dayjs(record.charter_date).format('DD/MM/YYYY')}`
    });
    setWorkflowModalVisible(true);
  };

  // Générer le lien de workflow (signature + paiement)
  const handleGenerateWorkflowLink = async (values) => {
    if (!selectedDocument) return;

    setGeneratingLink(true);
    try {
      // Générer un unique_link
      const uniqueLink = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Convertir la date au format ISO pour PostgreSQL
      let charterDateISO = selectedDocument.charter_date;
      if (charterDateISO && typeof charterDateISO === 'string') {
        // Si la date est au format DD/MM/YYYY, la convertir
        const parsed = dayjs(charterDateISO, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
        if (parsed.isValid()) {
          charterDateISO = parsed.format('YYYY-MM-DD');
        }
      }

      // Récupérer l'URL du PDF original (Google Drive)
      const originalPdfUrl = selectedDocument.documents?.Contrat?.pdf_url || null;

      // Uploader le PDF sur Supabase si nécessaire (pour éviter CORS côté client)
      let supabasePdfUrl = originalPdfUrl;
      if (originalPdfUrl && (workflowMode === 'unified' || workflowMode === 'signature')) {
        const uploadedUrl = await uploadPdfToSupabase(originalPdfUrl, uniqueLink, values.client_name);
        if (uploadedUrl) {
          supabasePdfUrl = uploadedUrl;
        }
      }

      // Créer le workflow en base
      const { data, error } = await supabase
        .from('contract_workflows')
        .insert({
          unique_link: uniqueLink,
          charter_id: selectedDocument.charter_id,
          client_name: values.client_name,
          client_email: values.client_email,
          charter_date: charterDateISO,
          amount: values.amount,
          description: values.description,
          pdf_url: supabasePdfUrl, // URL Supabase (ou originale si upload échoué)
          original_pdf_url: originalPdfUrl, // Garder l'original en référence
          status: 'draft',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Construire l'URL complète
      const baseUrl = window.location.origin;
      let fullUrl = `${baseUrl}/contract/${uniqueLink}`;

      // Ajouter le mode si ce n'est pas 'unified'
      if (workflowMode !== 'unified') {
        fullUrl += `?mode=${workflowMode}`;
      }

      setGeneratedWorkflowLink(fullUrl);

      // Mettre à jour le statut selon le mode
      const statusUpdate = workflowMode === 'signature' ? 'signature_sent'
        : workflowMode === 'payment' ? 'payment_sent'
        : 'link_sent';

      await supabase
        .from('contract_workflows')
        .update({ status: statusUpdate })
        .eq('id', data.id);

      message.success('Lien généré avec succès !');

    } catch (error) {
      console.error('Error generating workflow link:', error);
      message.error(`Erreur: ${error.message}`);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copier le lien workflow
  const copyWorkflowLink = () => {
    if (generatedWorkflowLink) {
      navigator.clipboard.writeText(generatedWorkflowLink);
      message.success('Lien copié dans le presse-papiers !');
    }
  };

  // Menu dropdown pour les actions de workflow
  const getWorkflowMenuItems = (record) => [
    {
      key: 'unified',
      label: 'Signature + Paiement',
      icon: <FormOutlined />,
      onClick: () => openWorkflowModal(record, 'unified')
    },
    {
      key: 'signature',
      label: 'Signature seule',
      icon: <EditOutlined />,
      onClick: () => openWorkflowModal(record, 'signature')
    },
    {
      key: 'payment',
      label: 'Paiement seul',
      icon: <DollarOutlined />,
      onClick: () => openWorkflowModal(record, 'payment')
    }
  ];

  // Colonnes du tableau
  const columns = [
    {
      title: 'Client',
      key: 'client',
      render: (_, record) => (
        <div>
          <Text strong style={{ display: 'block' }}>{record.client_name}</Text>
          {record.client_email && (
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.client_email}</Text>
          )}
        </div>
      ),
      sorter: (a, b) => (a.client_name || '').localeCompare(b.client_name || ''),
    },
    {
      title: 'Date Charter',
      dataIndex: 'charter_date',
      key: 'charter_date',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => new Date(a.charter_date) - new Date(b.charter_date),
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const categoryConfig = {
          bateaux: { color: 'blue', label: 'Bateaux' },
          jetski: { color: 'orange', label: 'Jetski' },
          seabobs: { color: 'cyan', label: 'Seabobs' },
          lunch: { color: 'green', label: 'Lunch' },
          custom: { color: 'purple', label: 'Custom' }
        };
        const config = categoryConfig[category] || { color: 'default', label: category };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: [
        { text: 'Bateaux', value: 'bateaux' },
        { text: 'Jetski', value: 'jetski' },
        { text: 'Seabobs', value: 'seabobs' },
        { text: 'Lunch', value: 'lunch' },
      ],
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => {
        const docs = Object.keys(record.documents || {});
        return (
          <Space>
            {docs.map(type => (
              <Tooltip key={type} title={type}>
                <Tag icon={<FileTextOutlined />}>
                  {type === 'Contrat' ? 'C' : type === 'Facturation' ? 'F' : type[0]}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Montant',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => amount ? `${amount.toLocaleString('fr-FR')} €` : '-',
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
      title: 'Statut Paiement',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status, record) => {
        const statusConfig = {
          pending: { color: 'default', icon: <ClockCircleOutlined />, label: 'En attente' },
          link_sent: { color: 'processing', icon: <SendOutlined />, label: 'Lien envoyé' },
          paid: { color: 'success', icon: <CheckCircleOutlined />, label: 'Payé' },
          failed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Échoué' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
      filters: [
        { text: 'En attente', value: 'pending' },
        { text: 'Lien envoyé', value: 'link_sent' },
        { text: 'Payé', value: 'paid' },
        { text: 'Échoué', value: 'failed' },
      ],
      onFilter: (value, record) => record.payment_status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space>
          {/* Dropdown pour générer des liens de workflow */}
          <Dropdown
            menu={{ items: getWorkflowMenuItems(record) }}
            trigger={['click']}
          >
            <Button
              type="primary"
              icon={<FormOutlined />}
              style={{ ...styles.actionBtn, background: '#722ed1', borderColor: '#722ed1' }}
            >
              Lien Client <DownOutlined />
            </Button>
          </Dropdown>

          {/* Bouton VoxPay (si configuré) */}
          {voxpayService && record.payment_status === 'pending' && (
            <Tooltip title="Générer lien VoxPay direct">
              <Button
                icon={<LinkOutlined />}
                onClick={() => openPaymentModal(record)}
                style={styles.actionBtn}
              >
                VoxPay
              </Button>
            </Tooltip>
          )}

          {/* Copier le lien existant */}
          {record.payment_link && (
            <Tooltip title="Copier le lien">
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyPaymentLink(record.payment_link)}
                style={styles.actionBtn}
              />
            </Tooltip>
          )}

          {/* Marquer comme payé */}
          {record.payment_status === 'link_sent' && (
            <Popconfirm
              title="Marquer comme payé ?"
              onConfirm={() => markAsPaid(record)}
              okText="Oui"
              cancelText="Non"
            >
              <Tooltip title="Marquer payé">
                <Button
                  type="default"
                  icon={<CheckCircleOutlined />}
                  style={{ ...styles.actionBtn, color: '#52c41a', borderColor: '#52c41a' }}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
            <div style={styles.headerIcon}>
              <DollarOutlined style={{ color: colors.textInverse }} />
            </div>
            <div>
              <h1 style={styles.headerTitle}>Suivi des Paiements</h1>
              <p style={styles.headerSubtitle}>
                Gérez les paiements et générez des liens VoxPay
              </p>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            style={{
              height: '44px',
              borderRadius: radius.lg,
            }}
          >
            Rafraîchir
          </Button>
        </div>

        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <Statistic
              title="Total Contrats"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </div>
          <div style={{ ...styles.statCard, background: '#fffbe6', borderColor: '#ffe58f' }}>
            <Statistic
              title="En attente"
              value={stats.pending}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </div>
          <div style={{ ...styles.statCard, background: '#e6f7ff', borderColor: '#91d5ff' }}>
            <Statistic
              title="Liens envoyés"
              value={stats.linkSent}
              prefix={<SendOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </div>
          <div style={{ ...styles.statCard, background: '#f6ffed', borderColor: '#b7eb8f' }}>
            <Statistic
              title="Payés"
              value={stats.paid}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </div>
          <div style={{ ...styles.statCard, background: '#f0f5ff', borderColor: '#adc6ff' }}>
            <Statistic
              title="Montant Total"
              value={stats.totalAmount}
              suffix="€"
              prefix={<DollarOutlined style={{ color: '#2f54eb' }} />}
              valueStyle={{ color: '#2f54eb' }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <div style={{ display: 'flex', gap: spacing.md, flex: 1 }}>
              <Input
                placeholder="Rechercher par client..."
                prefix={<SearchOutlined style={{ color: colors.textMuted }} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  maxWidth: '300px',
                  height: '44px',
                  borderRadius: radius.lg,
                }}
                allowClear
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 180, height: '44px' }}
              >
                <Option value="all">Tous les statuts</Option>
                <Option value="pending">En attente</Option>
                <Option value="link_sent">Lien envoyé</Option>
                <Option value="paid">Payé</Option>
                <Option value="failed">Échoué</Option>
              </Select>
            </div>
          </div>

          <Table
            dataSource={filteredDocuments}
            columns={columns}
            rowKey="charter_id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
            }}
            locale={{
              emptyText: <Empty description="Aucun contrat" />
            }}
          />
        </div>

        {/* Modal Génération Paiement */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <DollarOutlined style={{ fontSize: '24px', color: colors.bgDark }} />
              <span>Générer un lien de paiement</span>
            </div>
          }
          open={paymentModalVisible}
          onCancel={() => setPaymentModalVisible(false)}
          footer={null}
          width={500}
        >
          <Form
            form={paymentForm}
            layout="vertical"
            onFinish={handleGeneratePaymentLink}
          >
            <Form.Item
              name="client_name"
              label="Nom du client"
              rules={[{ required: true, message: 'Requis' }]}
            >
              <Input placeholder="Nom complet" />
            </Form.Item>

            <Form.Item
              name="client_email"
              label="Email du client"
              rules={[
                { required: true, message: 'Requis' },
                { type: 'email', message: 'Email invalide' }
              ]}
            >
              <Input placeholder="email@example.com" />
            </Form.Item>

            <Form.Item
              name="amount"
              label="Montant (EUR)"
              rules={[
                { required: true, message: 'Requis' },
                { type: 'number', min: 1, message: 'Le montant doit être supérieur à 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={value => value.replace(/\s?/g, '')}
                addonAfter="€"
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <Input.TextArea rows={2} placeholder="Description du paiement" />
            </Form.Item>

            <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.xl }}>
              <Button
                onClick={() => setPaymentModalVisible(false)}
                style={{ flex: 1, height: '44px', borderRadius: radius.lg }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={generatingLink}
                icon={<LinkOutlined />}
                style={{
                  flex: 2,
                  height: '44px',
                  borderRadius: radius.lg,
                  background: colors.bgDark
                }}
              >
                {generatingLink ? 'Génération...' : 'Générer le lien'}
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Modal Génération Lien Workflow */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
              <FormOutlined style={{ fontSize: '24px', color: '#722ed1' }} />
              <span>
                {workflowMode === 'unified' && 'Lien Signature + Paiement'}
                {workflowMode === 'signature' && 'Lien Signature seule'}
                {workflowMode === 'payment' && 'Lien Paiement seul'}
              </span>
            </div>
          }
          open={workflowModalVisible}
          onCancel={() => {
            setWorkflowModalVisible(false);
            setGeneratedWorkflowLink(null);
          }}
          footer={null}
          width={550}
        >
          {!generatedWorkflowLink ? (
            <Form
              form={workflowForm}
              layout="vertical"
              onFinish={handleGenerateWorkflowLink}
            >
              <div style={{
                background: '#f6f0ff',
                padding: spacing.lg,
                borderRadius: radius.lg,
                marginBottom: spacing.xl,
                border: '1px solid #d3adf7'
              }}>
                <Text>
                  {workflowMode === 'unified' && (
                    <>
                      <strong>Mode unifié :</strong> Le client recevra un lien unique pour signer le contrat
                      puis effectuer le paiement en une seule session.
                    </>
                  )}
                  {workflowMode === 'signature' && (
                    <>
                      <strong>Signature seule :</strong> Le client pourra uniquement signer le contrat.
                      Vous pourrez ensuite générer un lien de paiement séparé.
                    </>
                  )}
                  {workflowMode === 'payment' && (
                    <>
                      <strong>Paiement seul :</strong> Le client pourra directement effectuer le paiement
                      sans étape de signature.
                    </>
                  )}
                </Text>
              </div>

              <Form.Item
                name="client_name"
                label="Nom du client"
                rules={[{ required: true, message: 'Requis' }]}
              >
                <Input placeholder="Nom complet" />
              </Form.Item>

              <Form.Item
                name="client_email"
                label="Email du client"
                rules={[
                  { required: true, message: 'Requis' },
                  { type: 'email', message: 'Email invalide' }
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>

              <Form.Item
                name="amount"
                label="Montant (EUR)"
                rules={[
                  { required: true, message: 'Requis' },
                  { type: 'number', min: 0, message: 'Le montant doit être positif' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={value => value.replace(/\s?/g, '')}
                  addonAfter="€"
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description (optionnel)"
              >
                <Input.TextArea rows={2} placeholder="Description du contrat" />
              </Form.Item>

              <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.xl }}>
                <Button
                  onClick={() => setWorkflowModalVisible(false)}
                  style={{ flex: 1, height: '44px', borderRadius: radius.lg }}
                >
                  Annuler
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={generatingLink}
                  icon={<LinkOutlined />}
                  style={{
                    flex: 2,
                    height: '44px',
                    borderRadius: radius.lg,
                    background: '#722ed1',
                    borderColor: '#722ed1'
                  }}
                >
                  {generatingLink ? 'Génération...' : 'Générer le lien'}
                </Button>
              </div>
            </Form>
          ) : (
            <div>
              <div style={{
                background: '#f6ffed',
                padding: spacing.xl,
                borderRadius: radius.lg,
                marginBottom: spacing.xl,
                border: '1px solid #b7eb8f',
                textAlign: 'center'
              }}>
                <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: spacing.md }} />
                <Title level={4} style={{ margin: 0, marginBottom: spacing.sm }}>
                  Lien généré avec succès !
                </Title>
                <Text type="secondary">
                  Partagez ce lien avec votre client
                </Text>
              </div>

              <div style={{
                background: colors.bgTertiary,
                padding: spacing.lg,
                borderRadius: radius.lg,
                marginBottom: spacing.xl,
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}>
                {generatedWorkflowLink}
              </div>

              <div style={{ display: 'flex', gap: spacing.md }}>
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={copyWorkflowLink}
                  style={{
                    flex: 1,
                    height: '44px',
                    borderRadius: radius.lg,
                    background: '#722ed1',
                    borderColor: '#722ed1'
                  }}
                >
                  Copier le lien
                </Button>
                <Button
                  icon={<SendOutlined />}
                  onClick={() => {
                    const subject = encodeURIComponent(`Contrat à signer - ${selectedDocument?.client_name}`);
                    const body = encodeURIComponent(`Bonjour,\n\nVeuillez cliquer sur le lien suivant pour ${workflowMode === 'signature' ? 'signer votre contrat' : workflowMode === 'payment' ? 'effectuer votre paiement' : 'signer et payer votre contrat'} :\n\n${generatedWorkflowLink}\n\nCordialement`);
                    window.open(`mailto:${selectedDocument?.client_email}?subject=${subject}&body=${body}`);
                  }}
                  style={{
                    flex: 1,
                    height: '44px',
                    borderRadius: radius.lg
                  }}
                >
                  Envoyer par email
                </Button>
              </div>

              <Button
                type="link"
                onClick={() => {
                  setWorkflowModalVisible(false);
                  setGeneratedWorkflowLink(null);
                }}
                style={{ width: '100%', marginTop: spacing.md }}
              >
                Fermer
              </Button>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PaymentTracking;
